import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function getLastDayOfMonthText(date = {}) {
    const lastDay = new Date(date.year, date.month, 0);
    return `${lastDay.getDate()} de ${months[lastDay.getMonth()]} de ${lastDay.getFullYear()}`;
}

function getUserData() {
    try {
        return JSON.parse(localStorage.getItem('user')) || {};
    } catch (error) {
        return {};
    }
}

export function openMayorDailyBookPdf(dataProps) {
    const data = dataProps?.data || {};
    const body = data.body || [];
    const business = data.business || {};
    const user = getUserData();
    const doc = new jsPDF('p', 'pt', 'letter');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = { top: 88, right: 18, bottom: 35, left: 18 };
    const title = `Libro Diario Mayor ${data.bookType === 'consolidated' ? 'Consolidado' : 'Detallado'}`;

    const formatAmountCell = value => {
        const original = String(value ?? '').trim();
        const numericText = original.replace(/[$,\s]/g, '');
        const numericValue = Number(numericText);

        if (!original || (!Number.isNaN(numericValue) && numericValue === 0)) {
            return { content: '', symbol: '', amount: '' };
        }

        const formattedAmount = Number.isNaN(numericValue)
            ? original.replace('$', '').trim()
            : new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(numericValue);

        return {
            content: original,
            symbol: original.includes('$') || business.Currency === '$' ? '$' : '',
            amount: formattedAmount,
        };
    };

    const drawHeader = () => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(business.BusinessBillingName || '', pageWidth / 2, 29, { align: 'center' });
        doc.text(`${title} al ${getLastDayOfMonthText(data.date)}`, pageWidth / 2, 42, { align: 'center' });
        if (business.Currency === '$') {
            doc.text('US DOLAR', pageWidth / 2, 55, { align: 'center' });
        }
    };

    const isAccountRow = item => /^\d{3,}$/.test(String(item.main_column ?? '').trim());
    const cellValue = (value, accountRow) => accountRow
        ? { content: value ?? '', styles: { fontStyle: 'bold' } }
        : value ?? '';
    const conceptValue = (value, accountRow) => ({
        content: value ?? '',
        styles: {
            fontSize: 8,
            ...(accountRow ? { fontStyle: 'bold' } : {}),
        },
    });
    const amountValue = (value, accountRow) => {
        const amount = formatAmountCell(value);
        return accountRow
            ? { ...amount, styles: { fontStyle: 'bold', fontSize: 8 } }
            : amount;
    };

    autoTable(doc, {
        startY: margin.top,
        margin,
        head: [['Fecha', 'Concepto', '', 'Inicial', 'Cargo', 'Abono', 'Saldo']],
        body: body.map(item => {
            const accountRow = isAccountRow(item);
            const concept = item.ID_Accounting_Item
                ? `${item.ID_Accounting_Item}\n${item.concept ?? ''}`
                : item.concept ?? '';

            return [
                cellValue(item.main_column, accountRow),
                conceptValue(concept, accountRow),
                cellValue('', accountRow),
                amountValue(item.initial_balance, accountRow),
                amountValue(item.charges, accountRow),
                amountValue(item.payments, accountRow),
                amountValue(item.current_balance, accountRow),
            ];
        }),
        theme: 'plain',
        styles: {
            font: 'helvetica',
            fontSize: 9,
            cellPadding: { top: 4.5, right: 2.5, bottom: 4.5, left: 2.5 },
            overflow: 'linebreak',
            valign: 'top',
            lineColor: 0,
            textColor: 0,
        },
        headStyles: {
            fontSize: 9,
            fontStyle: 'normal',
            lineWidth: { top: 0.6, bottom: 0.6 },
            lineColor: 0,
        },
        columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 260 },
            2: { cellWidth: 40 },
            3: { cellWidth: 55 },
            4: { cellWidth: 55 },
            5: { cellWidth: 55 },
            6: { cellWidth: 55 },
        },
        didParseCell: hookData => {
            const raw = hookData.cell.raw;
            if (hookData.section === 'body' && hookData.column.index >= 3 && hookData.column.index <= 6 && raw?.amount !== undefined) {
                hookData.cell.text = raw.amount ? [raw.amount] : [];
                hookData.cell.styles.textColor = [255, 255, 255];
            }
        },
        willDrawCell: hookData => {
            const raw = hookData.cell.raw;
            if (hookData.section !== 'body' || hookData.column.index < 3 || hookData.column.index > 6 || raw?.amount === undefined) {
                return;
            }

            hookData.cell.text = [];
            doc.setFont('helvetica', raw.styles?.fontStyle === 'bold' ? 'bold' : 'normal');
            doc.setFontSize(raw.styles?.fontSize || 9);
            doc.setTextColor(0, 0, 0);
            if (raw.amount) {
                doc.text(raw.symbol, hookData.cell.x + 2.5, hookData.cell.y + 4.5 + 10);
                doc.text(raw.amount, hookData.cell.x + hookData.cell.width - 2.5, hookData.cell.y + 4.5 + 10, { align: 'right' });
            }
            doc.setTextColor(255, 255, 255);
        },
        didDrawPage: drawHeader,
    });

    let signatureY = (doc.lastAutoTable?.finalY || margin.top) + 42;
    if (signatureY + 45 > pageHeight - margin.bottom) {
        doc.addPage();
        drawHeader();
        signatureY = margin.top;
    }

    doc.setFontSize(9);
    const drawSignature = (x, fallback, name, additional, role) => {
        doc.text('__________________________________', x, signatureY, { align: 'center' });
        doc.text(name || fallback, x, signatureY + 12, { align: 'center' });
        doc.text(additional || '', x, signatureY + 24, { align: 'center' });
        doc.text(name ? role : '', x, signatureY + 36, { align: 'center' });
    };
    drawSignature(pageWidth * 0.28, 'Contador', user.AccountantName, user.AccountantAdditional, 'Contador');
    drawSignature(pageWidth * 0.72, 'Auditor', user.AuditorName, user.AuditorAdditional, 'Auditor');

    const pageCount = doc.internal.getNumberOfPages();
    for (let page = 1; page <= pageCount; page++) {
        doc.setPage(page);
        doc.setFontSize(9);
        doc.text(`Pág. ${page}/${pageCount}`, pageWidth - margin.right, 68, { align: 'right' });
    }

    doc.autoPrint({ variant: 'non-conform' });
    window.open(doc.output('bloburl'));
    return doc;
}
